import Phaser from "phaser";
import {
  type Direction,
  PROJECTILE_MAX_TILES,
  PROJECTILE_SPEED,
  TILE_SIZE,
} from "../config";
import { SPRITE_FRAMES, SPRITESHEET_KEY, SPRITESHEET_TILE } from "../sprites";
import type { WhichPlayer } from "./Player";

const PROJECTILE_SCALE = (TILE_SIZE * 0.7) / SPRITESHEET_TILE; // a bit smaller than a full tile

/**
 * A moving fire projectile. Lives in a Phaser physics group so the scene can
 * use overlap detection against player sprites. Self-destructs after travelling
 * PROJECTILE_MAX_TILES tiles — fixes the 2011 firegun bug where 3 tiles were
 * spawned simultaneously at fixed offsets and could land off-screen.
 */
export class Projectile extends Phaser.Physics.Arcade.Sprite {
  readonly damage: number;
  readonly ownerWhich: WhichPlayer;

  constructor(
    scene: Phaser.Scene,
    startCol: number,
    startRow: number,
    direction: Direction,
    damage: number,
    ownerWhich: WhichPlayer,
  ) {
    super(
      scene,
      startCol * TILE_SIZE + TILE_SIZE / 2,
      startRow * TILE_SIZE + TILE_SIZE / 2,
      SPRITESHEET_KEY,
      SPRITE_FRAMES.projectile,
    );
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.setScale(PROJECTILE_SCALE);
    this.setDepth(8);

    this.damage = damage;
    this.ownerWhich = ownerWhich;

    this.setVelocity(
      direction.x * PROJECTILE_SPEED,
      direction.y * PROJECTILE_SPEED,
    );

    scene.tweens.add({
      targets: this,
      scale: { from: PROJECTILE_SCALE, to: PROJECTILE_SCALE * 1.25 },
      duration: 140,
      yoyo: true,
      repeat: -1,
    });

    // Time-of-flight based lifespan. Cleaner than per-frame distance accounting.
    const lifeMs = ((PROJECTILE_MAX_TILES * TILE_SIZE) / PROJECTILE_SPEED) * 1000;
    scene.time.delayedCall(lifeMs, () => {
      if (this.active) this.destroy();
    });
  }
}
